import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AppAssessmentsService, AuthService, QueryService, WorkspaceService } from '@pt/api-client';
import {
  AppAssessmentDocCreateRequest,
  AppAssessmentDocUpdateRequest,
  DocRenameRequest,
  WorkspaceItem,
  WorkspaceItemType,
  WorkspaceMoveRequest
} from '@primatoo-ms-organization';
import { of } from 'rxjs';
import * as AppAssessmentActions from '../actions/app-assessment.actions';
import { WidgetDialogService } from '../../services/widget-dialog.service';
import { PtCoreActions } from '@pt/pt-core';
import { DialogService } from '../../../services';
import { TranslateService } from '@ngx-translate/core';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import * as AiAssessmentActions from '../actions/ai-assessment.actions';

@Injectable()
export class AppAssessmentEffects {

  publishAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.publishAppAssessment),
      switchMap((action: { docId: number, skipBack?: boolean }) =>
        this.appAssessmentService.publish(action.docId).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                AppAssessmentActions.publishAppAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                AppAssessmentActions.publishAppAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(AppAssessmentActions.publishAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  publishAppAssessmentWithConfirm = createEffect(() =>
    this.actions$.pipe(
      ofType(AppAssessmentActions.publishAppAssessmentWithConfirm),
      switchMap((action: { docId: number, skipBack?: boolean, hideInfo?: boolean }) => {
        const title = 'TITLE.APP_ASSESSMENT_PUBLISH';
        const message = 'MESSAGE.APP_ASSESSMENT_PUBLISH';
        // const info = 'MESSAGE.ON_END_OPERATION_COME_BACK';

        return this.widgetDialogService.runConfirmDialog(
          title,
          message,
          // !action.hideInfo ? info : null
        ).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(AppAssessmentActions.publishAppAssessment({
                docId: action?.docId,
                skipBack: action?.skipBack
              }));
            }
          })
        );
      })
    ), {dispatch: false}
  );
  validateDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.validateAppAssessment),
      switchMap((action: { docId: number, request: ValidateDocumentRequest, skipBack?: boolean }) =>
        this.appAssessmentService.validate(action.docId, action.request).pipe(
          switchMap(() => {
            if (action?.skipBack) {
              return [
                AppAssessmentActions.validateAppAssessmentSuccess(),
                PtCoreActions.showOperationSuccessNotification()
              ];
            } else {
              return [
                AppAssessmentActions.validateAppAssessmentSuccess(),
                PtCoreActions.navigateBack({
                  hop: -1,
                  queryParams: {
                    opened: true,
                    id: action.docId
                  }
                }),
                PtCoreActions.showOperationSuccessNotification()
              ];
            }
          }),
          catchError(err =>
            of(AppAssessmentActions.validateAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  validateDocumentWithConfirm$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(AppAssessmentActions.validateAppAssessmentWithConfirm),
        map((action: { docId: number, skipBack?: boolean }) => {
          const title = 'TITLE.APP_ASSESSMENT_VALIDATE';
          const message = 'MESSAGE.APP_ASSESSMENT_VALIDATE';
          return this.dialogService.runValidateRequestDialog(title, message,).subscribe((request) => {
              if (request) {
                this.store.dispatch(AppAssessmentActions.validateAppAssessment({
                  docId: action?.docId,
                  request,
                  skipBack: action.skipBack
                }));
              } else {
                this.store.dispatch(AppAssessmentActions.validateAppAssessmentFailed({ error: null }))
              }
            }
          );
        })
      );
    }, {dispatch: false}
  );

  reopenAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.reopenAppAssessment),
      switchMap((action: { id: number }) =>
        this.appAssessmentService.reopen(action.id).pipe(
          switchMap(() => {
            return [
              PtCoreActions.showOperationSuccessNotification(),
              AppAssessmentActions.reopenAppAssessmentSuccess({id: action.id})
            ];
          }),
          catchError(err =>
            of(AppAssessmentActions.reopenAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  reopenAppAssessmentWithConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppAssessmentActions.reopenAppAssessmentWithConfirm),
      switchMap((action) => {
        const title = 'TITLE.APP_ASSESSMENT_REOPEN';
        const message = 'MESSAGE.APP_ASSESSMENT_REOPEN';

        return this.widgetDialogService.runConfirmDialog(title, message).pipe(
          map((response) => {
            if (response) {
              this.store.dispatch(AppAssessmentActions.reopenAppAssessment({
                id: action?.id
              }));
            }
          })
        );

      })
    ), {dispatch: false}
  );
  updateAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.updateAppAssessment),
      switchMap((action: { id: number, request: AppAssessmentDocUpdateRequest }) =>
        this.appAssessmentService.update(action.id, action.request).pipe(
          map((response: UpdateDocResponse) =>
            AppAssessmentActions.updateAppAssessmentSuccess({response})
          ),
          catchError(err =>
            of(AppAssessmentActions.updateAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  renameAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.renameAppAssessment),
      switchMap((action: { id: number, request: DocRenameRequest }) =>
        this.appAssessmentService.rename(action.id, action.request).pipe(
          map(() =>
            AppAssessmentActions.renameAppAssessmentSuccess({id: action.id})
          ),
          catchError(err =>
            of(AppAssessmentActions.renameAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });
  createAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.createAppAssessment),
      switchMap((action: { request: AppAssessmentDocCreateRequest }) =>
        this.appAssessmentService.create(action.request).pipe(
          map((response: CreateDocResponse) =>
            AppAssessmentActions.createAppAssessmentSuccess({id: response.docId})
          ),
          catchError(err =>
            of(AppAssessmentActions.createAppAssessmentFailed({error: err.message}))
          )
        )
      )
    );
  });

  // updateAppAssessmentSuccess$ = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(AppAssessmentActions.updateAppAssessmentSuccess),
  //     switchMap((action: { response: AppAssessmentDocUpdateResponse }) =>
  //       this.appAssessmentService.getTemplate(action.response.docId).pipe(
  //         map((response: TemplateWithUser) =>
  //           NgrxWidgetActions.updateStoreTemplate({
  //             template: response as AppAssessmentEditable,
  //             docType: DocumentEditorType.APP_ASSESSMENT
  //           })
  //         ),
  //         catchError(() =>
  //           of(NgrxWidgetActions.updateStoreTemplate({template: null, docType: null}))
  //         )
  //       )
  //     )
  //   );
  // });
  moveAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.moveAppAssessment),
      switchMap((action: { id: number, workspaceMoveRequest: WorkspaceMoveRequest }) =>
        this.workspaceService.move(action.id, WorkspaceItemType.APP_ASSESSMENT, action.workspaceMoveRequest).pipe(
          map((item: WorkspaceItem) =>
            AppAssessmentActions.moveAppAssessmentSuccess({item})
          ),
          catchError(err => of(
            AppAssessmentActions.moveAppAssessmentFailed({error: err.message})
          ))
        )
      )
    );
  });
  duplicateAppAssessment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppAssessmentActions.duplicateAppAssessment),
      switchMap((action) =>
        this.appAssessmentService.duplicate(action.id, action.request).pipe(
          map((response) =>
            AppAssessmentActions.duplicateAppAssessmentSuccess({
              response
            })
          ),
          catchError(err => of(
            AppAssessmentActions.duplicateAppAssessmentFailed({
              error: err?.message
            })
          ))
        )
      )
    )
  );
  deleteAppAssessment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppAssessmentActions.deleteAppAssessment),
      switchMap((action) =>
        this.appAssessmentService.delete(action.id).pipe(
          map(() => AppAssessmentActions.deleteAppAssessmentSuccess()),
          catchError(err => of(
            AppAssessmentActions.deleteAppAssessmentFailed({
              error: err.message
            })
          ))
        )
      ));
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private authService: AuthService,
    private queryService: QueryService,
    private widgetDialogService: WidgetDialogService,
    private appAssessmentService: AppAssessmentsService,
    private workspaceService: WorkspaceService,
    private dialogService: DialogService,
    private translateService: TranslateService,
  ) {
  }

  // deleteAppAssessmentWithVersion$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AppAssessmentActions.deleteAppAssessmentWithVersion),
  //     switchMap((action: { id: number, version: number }) => {
  //         const title = this.translateService.stream('TITLE.APP_ASSESSMENT_DELETE');
  //         const message = this.translateService.stream('MESSAGE.APP_ASSESSMENT_DELETE');
  //         return this.dialogService.runConfirmDialogWithValidation({
  //           title,
  //           message,
  //           dialogType: DialogType.DELETE,
  //           size: 'md'
  //         }).pipe(
  //           map((response) => {
  //             if (response) {
  //               this.appAssessmentService.deleteByVersion(action.id, action.version).subscribe(
  //                 (success) => {
  //                   this.store.dispatch(AppAssessmentActions.deleteAppAssessmentWithVersionSuccess());
  //                   this.store.dispatch(PtCoreActions.navigateBack({
  //                     hop: -1,
  //                     queryParams: {
  //                       opened: true,
  //                       id: action.id
  //                     }
  //                   }));
  //                   this.store.dispatch(PtCoreActions.showOperationSuccessNotification());
  //                 },
  //                 (error) => AppAssessmentActions.deleteAppAssessmentWithVersionFailed({
  //                   error: error.message
  //                 })
  //               )
  //             }
  //           })
  //         );
  //       }
  //     )
  //   ), {dispatch: false}
  // );
}
